// ─────────────────────────────────────────────
//  Sandbox query engine — where() matching and orderBy() sorting.
//
//  Pure functions over plain rows. Deliberately mirrors Firestore's observable
//  behaviour rather than inventing something friendlier, because the app was
//  written against Firestore:
//    • a document MISSING the filtered field never matches
//    • a document MISSING the sort field sorts last instead of vanishing
//    • strings compare by code unit, so "Bob" precedes "alice"
//  Getting these wrong produces a quietly wrong board, not an error.
// ─────────────────────────────────────────────
const MISSING = Symbol("missing");

export function getField(data, path) {
  if (!data) return undefined;
  return path.split(".").reduce(
    (acc, key) => (acc == null ? undefined : acc[key]),
    data
  );
}

const readField = (data, path) => {
  const v = getField(data, path);
  return v === undefined ? MISSING : v;
};

export const whereConstraint = (field, op, value) => ({ kind: "where", field, op, value });
export const orderByConstraint = (field, dir = "asc") => ({ kind: "orderBy", field, dir });
export const limitConstraint = (n) => ({ kind: "limit", n });

// Timestamps compare by millisecond value; everything else by JS ordering.
const comparable = (v) =>
  v && typeof v.toMillis === "function" ? v.toMillis()
    : v instanceof Date ? v.getTime()
    : v;

const eq = (a, b) => {
  const x = comparable(a), y = comparable(b);
  if (Array.isArray(x) && Array.isArray(y)) {
    return x.length === y.length && x.every((v, i) => eq(v, y[i]));
  }
  return x === y;
};

const matches = (value, op, target) => {
  if (value === MISSING) return false; // Firestore never matches a missing field
  const v = comparable(value);
  const t = comparable(target);
  switch (op) {
    case "==": return eq(value, target);
    case "!=": return !eq(value, target);
    case "<": return v < t;
    case "<=": return v <= t;
    case ">": return v > t;
    case ">=": return v >= t;
    case "in": return Array.isArray(target) && target.some((x) => eq(value, x));
    case "not-in": return Array.isArray(target) && !target.some((x) => eq(value, x));
    case "array-contains":
      return Array.isArray(value) && value.some((x) => eq(x, target));
    case "array-contains-any":
      return Array.isArray(value) && Array.isArray(target)
        && value.some((x) => target.some((y) => eq(x, y)));
    default:
      throw new Error(`[sandbox] unsupported where operator: ${op}`);
  }
};

const compare = (a, b) => {
  if (a === MISSING && b === MISSING) return 0;
  if (a === MISSING) return 1;   // missing sorts last
  if (b === MISSING) return -1;
  const x = comparable(a), y = comparable(b);
  if (x === y) return 0;
  return x < y ? -1 : 1;
};

export function applyConstraints(docs, constraints = []) {
  const wheres = constraints.filter((c) => c.kind === "where");
  const orders = constraints.filter((c) => c.kind === "orderBy");

  let rows = docs.filter((row) =>
    wheres.every((c) => matches(readField(row.data, c.field), c.op, c.value))
  );

  if (orders.length) {
    rows = [...rows].sort((ra, rb) => {
      for (const o of orders) {
        const r = compare(readField(ra.data, o.field), readField(rb.data, o.field));
        if (r !== 0) return o.dir === "desc" ? -r : r;
      }
      return 0;
    });
  }

  // limit() applies AFTER filtering and ordering, as it does in Firestore.
  // Applying it earlier would return an arbitrary subset rather than the first
  // N of the ordered result — a difference that produces a plausible-looking
  // but wrong list instead of an error.
  const cap = constraints.find((c) => c.kind === "limit");
  if (cap && Number.isInteger(cap.n) && cap.n >= 0) rows = rows.slice(0, cap.n);

  return rows;
}
